import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { bidInterest, convertToDecimal } from "services";

export function usePrivatePDWrapperController({ propertyData }: any) {
  const [authModelOpen, setAuthModelOpen] = useState(false);
  const [acknowledgementOpen, setAcknowledgementOpen] = useState(false);
  const [acknowledgementDetails, setAcknowledgementDetails] = useState({
    name: "",
    mobile: "",
    email: "",
    isFooter: false,
  });
  const [errorMessage, setErrorMessage] = useState("");

  const isLoggedIn = useSelector((state: any) => state.auth.user);

  const area = propertyData?.posted_properties_raw_data?.builtUpArea
    ? propertyData?.posted_properties_raw_data?.builtUpArea
    : propertyData?.posted_properties_raw_data?.carpetArea;

  const balcony = propertyData?.posted_properties_raw_data?.noOfBalcony?.value
    ? propertyData?.posted_properties_raw_data?.noOfBalcony?.value === "1"
      ? `${propertyData?.posted_properties_raw_data?.noOfBalcony?.value} Balcony`
      : `${propertyData?.posted_properties_raw_data?.noOfBalcony?.value} Balconies`
    : "";

  const bathroom = propertyData?.posted_properties_raw_data?.noOfBathroom?.value
    ? propertyData?.posted_properties_raw_data?.noOfBathroom?.value === "1"
      ? `${propertyData?.posted_properties_raw_data?.noOfBathroom?.value} Bathroom`
      : `${propertyData?.posted_properties_raw_data?.noOfBathroom?.value} Bathrooms`
    : "";

  const parking = propertyData?.posted_properties_raw_data?.noOfParking?.value
    ? propertyData?.posted_properties_raw_data?.noOfParking?.value === "1"
      ? `${propertyData?.posted_properties_raw_data?.noOfParking?.value} Parking`
      : `${propertyData?.posted_properties_raw_data?.noOfParking?.value} Parkings`
    : "";

  const propertyAge =
    propertyData?.posted_properties_raw_data?.ageOfPropertyAdv;
  const totalFloors = propertyData?.posted_properties_raw_data?.totalFloorAdv;

  const result = [bathroom, parking, balcony].filter(Boolean).join(", ");
  propertyData["totalFloors"] = totalFloors;
  propertyData["configuration"] = result;
  propertyData["ageOfProperty"] = propertyAge;

  propertyData.possession_type =
    propertyData?.posted_properties_raw_data?.constructionStatus?.value ===
    "ready_to_move"
      ? "Ready To Move"
      : propertyData?.posted_properties_raw_data?.possessionDate;

  propertyData.area = propertyData?.area ? propertyData?.area : area;

  const handleInterested = useCallback(async () => {
    const payload: {
      name: string;
      mobile_number?: string;
      email?: string;
      property_id: string;
    } = {
      name: acknowledgementDetails.name,
      property_id: propertyData.listing_id,
    };

    if (acknowledgementDetails?.mobile?.length === 10) {
      payload.mobile_number = acknowledgementDetails.mobile;
    }

    if (
      acknowledgementDetails.email &&
      acknowledgementDetails?.email?.length > 3
    ) {
      payload.email = acknowledgementDetails.email;
    }

    try {
      await bidInterest(payload);
      setAcknowledgementOpen(true);
      setAcknowledgementDetails({
        name: "",
        mobile: "",
        email: "",
        isFooter: false,
      });
      setErrorMessage("");
    } catch (error) {
      console.error("Error handling bid interest:", error);
      setErrorMessage("Failed to submit interest. Please try again.");
    }
  }, [acknowledgementDetails, propertyData.listing_id]);

  const coordinates = useMemo(() => {
    const locationCoordinates = propertyData?.location_coordinates;
    let _coordinates = {
      isCoordinatesFound: false,
      value: ["na", "na"],
    };
    if (
      locationCoordinates &&
      !locationCoordinates?.toLowerCase()?.includes("na") &&
      !locationCoordinates?.toLowerCase()?.includes("no") &&
      !locationCoordinates?.toLowerCase()?.includes("contact no") &&
      !locationCoordinates?.toLowerCase()?.includes("null")
    ) {
      if (locationCoordinates.includes(",")) {
        const foundCoordinates = locationCoordinates
          .split(",")
          .map((coord: string) =>
            coord
              .toString()
              .replace(/[^\d.-]/g, "")
              .trim(),
          )
          .map((coord: string) => {
            return convertToDecimal(coord.replace(/[nesw]/gi, "").trim());
          });
        const cleanCoords = foundCoordinates?.filter(
          (value: any) => !isNaN(value),
        );

        if (Array?.isArray(cleanCoords) && cleanCoords?.length === 2) {
          _coordinates = {
            isCoordinatesFound: true,
            value: cleanCoords,
          };
        } else {
          _coordinates = {
            isCoordinatesFound: false,
            value: cleanCoords,
          };
        }
      } else if (locationCoordinates && locationCoordinates.includes(" ")) {
        // Handle space-separated coordinates (e.g., 30.89 75.89)
        const foundCoordinates = locationCoordinates
          .split(" ")
          .map((coord: string) =>
            convertToDecimal(
              coord
                .toString()
                .replace(/[^\d.-]/g, "")
                .trim(),
            ),
          );
        const cleanCoords = foundCoordinates?.filter(
          (value: any) => !isNaN(value),
        );
        if (Array?.isArray(cleanCoords) && cleanCoords?.length === 2) {
          _coordinates = {
            isCoordinatesFound: true,
            value: cleanCoords,
          };
        } else {
          _coordinates = {
            isCoordinatesFound: false,
            value: cleanCoords,
          };
        }
      }
    }

    return _coordinates;
  }, [propertyData?.location_coordinates]);

  return {
    setAuthModelOpen,
    setAcknowledgementOpen,
    handleInterested,
    isLoggedIn,
    authModelOpen,
    acknowledgementOpen,
    acknowledgementDetails,
    setAcknowledgementDetails,
    coordinates,
    errorMessage,
  };
}
